// ─────────────────────────────────────────────
//  FolderTreeNode
//  Recursive node for the SharePoint folder tree.
//  Uses a context so it doesn't need 12 props.
// ─────────────────────────────────────────────

import * as React from "react";
import { Spinner, makeStyles, tokens } from "@fluentui/react-components";
import {
  CheckmarkCircle16Filled,
  ChevronDown20Regular,
  ChevronRight20Regular,
} from "@fluentui/react-icons";
import type { FolderItem } from "../../types/sharepoint";

// ── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  row: {
    alignItems: "center",
    backgroundColor: "transparent",
    border: 0,
    borderRadius: tokens.borderRadiusSmall,
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    display: "flex",
    fontFamily: tokens.fontFamilyBase,
    fontSize: tokens.fontSizeBase200,
    gap: tokens.spacingHorizontalXXS,
    height: "28px",
    lineHeight: tokens.lineHeightBase200,
    margin: `1px ${tokens.spacingHorizontalXXS}`,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    textAlign: "left",
    transition: "background-color 100ms ease",
    width: "calc(100% - 8px)",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },

  rowSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    ":hover": { backgroundColor: tokens.colorBrandBackground2Hover },
  },

  chevron: {
    alignItems: "center",
    display: "inline-flex",
    flexShrink: 0,
    height: "16px",
    justifyContent: "center",
    width: "16px",
  },

  folderIcon: {
    flexShrink: 0,
    height: "14px",
    objectFit: "contain",
    width: "14px",
  },

  name: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  checkmark: {
    alignItems: "center",
    color: tokens.colorBrandForeground1,
    display: "inline-flex",
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalXXS,
  },
});

// ── Context ──────────────────────────────────────────────────────────────────

interface FolderTreeCtx {
  selectedFolderId: string;
  expandedFolders: Record<string, boolean>;
  loadingFolderIds: Record<string, boolean>;
  onSelect: (node: FolderItem) => void;
  onToggle: (folderId: string) => void;
  onExpand: (node: FolderItem) => void;
}

const FolderTreeContext = React.createContext<FolderTreeCtx>({
  selectedFolderId: "",
  expandedFolders: {},
  loadingFolderIds: {},
  onSelect: () => undefined,
  onToggle: () => undefined,
  onExpand: () => undefined,
});

export function FolderTreeProvider({
  children,
  ...value
}: React.PropsWithChildren<FolderTreeCtx>) {
  return <FolderTreeContext.Provider value={value}>{children}</FolderTreeContext.Provider>;
}

// ── Node ─────────────────────────────────────────────────────────────────────

interface FolderTreeNodeProps {
  node: FolderItem;
  level: number;
}

export const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({ node, level }) => {
  const styles = useStyles();
  const { selectedFolderId, expandedFolders, loadingFolderIds, onSelect, onToggle, onExpand } =
    React.useContext(FolderTreeContext);

  const childrenLoaded = node.children !== undefined;
  const hasChildren = !childrenLoaded || node.children!.length > 0;
  const isExpanded = !!expandedFolders[node.id];
  const isSelected = selectedFolderId === node.id;
  const isLoading = !!loadingFolderIds[node.id];

  const handleClick = () => {
    onSelect(node);
    if (!childrenLoaded && !isLoading) {
      onExpand(node);
    } else if (childrenLoaded && node.children!.length > 0) {
      onToggle(node.id);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.row} ${isSelected ? styles.rowSelected : ""}`}
        style={{ paddingLeft: 8 + level * 16 }}
        onClick={handleClick}
        aria-expanded={hasChildren ? isExpanded : undefined}
        title={node.name}
      >
        <span className={styles.chevron}>
          {hasChildren &&
            (isLoading ? (
              <Spinner size="extra-tiny" />
            ) : isExpanded ? (
              <ChevronDown20Regular fontSize={14} />
            ) : (
              <ChevronRight20Regular fontSize={14} />
            ))}
        </span>

        <img
          className={styles.folderIcon}
          src="assets/folder-icon.png"
          alt=""
          aria-hidden="true"
        />

        <span className={styles.name}>{node.name}</span>

        {isSelected && (
          <span className={styles.checkmark} aria-hidden="true">
            <CheckmarkCircle16Filled fontSize={14} />
          </span>
        )}
      </button>

      {hasChildren && isExpanded && childrenLoaded &&
        node.children?.map((child) => (
          <FolderTreeNode key={child.id} node={child} level={level + 1} />
        ))}
    </>
  );
};
